import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Info,
  ExpandMore,
  Security,
  Description,
  Gavel,
  ThumbUp,
  ThumbDown,
  HelpOutline
} from '@mui/icons-material';
import aiService from '../../services/aiService';

const AIComplianceChecker = ({ 
  report,
  inspectionType = 'general',
  buildingCodes = 'Zimbabwe National Building Code',
  projectSpecifications = {},
  onComplianceChecked,
  autoCheck = false
}) => {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const checkCompliance = async () => {
    if (!report || report.trim().length === 0) {
      setError('Report content is required for compliance checking');
      return;
    }

    setChecking(true);
    setError('');

    try {
      const complianceResult = await aiService.checkInspectionCompliance({
        report,
        inspectionType,
        buildingCodes,
        projectSpecifications
      });

      setResult(complianceResult);

      if (onComplianceChecked) {
        onComplianceChecked(complianceResult);
      }
    } catch (err) {
      console.error('Compliance check error:', err);
      setError('Failed to check compliance. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  React.useEffect(() => {
    if (autoCheck && report) {
      checkCompliance();
    }
  }, [autoCheck, report]);

  const getComplianceColor = (status) => {
    switch (status) {
      case 'COMPLIANT':
        return 'success';
      case 'NON_COMPLIANT':
        return 'error';
      case 'REQUIRES_REVIEW':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getComplianceIcon = (status) => {
    switch (status) {
      case 'COMPLIANT':
        return <CheckCircle />;
      case 'NON_COMPLIANT':
        return <Error />;
      case 'REQUIRES_REVIEW':
        return <Warning />;
      default:
        return <HelpOutline />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL':
        return 'error';
      case 'MAJOR':
        return 'warning';
      case 'MINOR':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL':
        return <Error />;
      case 'MAJOR':
        return <Warning />;
      case 'MINOR':
        return <Info />;
      default:
        return <Info />;
    }
  };

  const getApprovalIcon = (recommendation) => {
    switch (recommendation) {
      case 'APPROVE':
        return <ThumbUp />;
      case 'REJECT':
        return <ThumbDown />;
      case 'CONDITIONAL_APPROVE':
        return <HelpOutline />;
      default:
        return <HelpOutline />;
    }
  };

  const getApprovalColor = (recommendation) => {
    switch (recommendation) {
      case 'APPROVE':
        return 'success';
      case 'REJECT':
        return 'error';
      case 'CONDITIONAL_APPROVE':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!result && !checking && report && (
        <Paper sx={{ p: 2, bgcolor: 'info.50', border: 1, borderColor: 'info.200' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Security color="info" />
            <Typography variant="subtitle2" color="info.main">
              Compliance Checker
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Check your inspection report against building codes, project specifications, and industry standards.
          </Typography>
          <Button
            variant="contained"
            onClick={checkCompliance}
            startIcon={<Gavel />}
            fullWidth
          >
            Check Compliance
          </Button>
        </Paper>
      )}

      {checking && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            AI is checking compliance against building codes and standards...
          </Typography>
        </Paper>
      )}

      {result && (
        <Box>
          {/* Overall Compliance Status */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">
                    Compliance Status
                  </Typography>
                  <Chip
                    label={result.overallCompliance}
                    color={getComplianceColor(result.overallCompliance)}
                    icon={getComplianceIcon(result.overallCompliance)}
                  />
                </Box>
                <Chip
                  label={`${result.approvalRecommendation}`}
                  color={getApprovalColor(result.approvalRecommendation)}
                  icon={getApprovalIcon(result.approvalRecommendation)}
                  variant="outlined"
                />
              </Box>

              {/* Compliance Score */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Compliance Score
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {result.complianceScore}/100
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={result.complianceScore}
                  color={
                    result.complianceScore >= 80 ? 'success' :
                    result.complianceScore >= 60 ? 'warning' : 'error'
                  }
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Quick Stats */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {result.issues && (
                  <>
                    <Chip
                      label={`${result.issues.filter(i => i.severity === 'CRITICAL').length} Critical`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                    <Chip
                      label={`${result.issues.filter(i => i.severity === 'MAJOR').length} Major`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                    <Chip
                      label={`${result.issues.filter(i => i.severity === 'MINOR').length} Minor`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Issues */}
          {result.issues && result.issues.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning color="warning" />
                  <Typography variant="subtitle2">
                    Compliance Issues ({result.issues.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {result.issues.map((issue, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getSeverityIcon(issue.severity)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip
                                label={issue.severity}
                                size="small"
                                color={getSeverityColor(issue.severity)}
                              />
                              <Chip
                                label={issue.category}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                {issue.description}
                              </Typography>
                              <Typography variant="body2" color="primary" sx={{ mb: 0.5 }}>
                                <strong>Recommendation:</strong> {issue.recommendation}
                              </Typography>
                              {issue.codeReference && (
                                <Typography variant="caption" color="text.secondary">
                                  <Gavel fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                  Reference: {issue.codeReference}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < result.issues.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Missing Information */}
          {result.missingInformation && result.missingInformation.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Description color="info" />
                  <Typography variant="subtitle2">
                    Missing Information ({result.missingInformation.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {result.missingInformation.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Info color="info" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Suggestions */}
          {result.suggestions && result.suggestions.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  <Typography variant="subtitle2">
                    Improvement Suggestions ({result.suggestions.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {result.suggestions.map((suggestion, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={suggestion} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Re-check Button */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={checkCompliance}
              startIcon={<Gavel />}
              disabled={checking}
            >
              Re-check Compliance
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AIComplianceChecker;
